/**
 * DemoVideoSection
 *
 * Landing page section embedding a demo video with theme-aware thumbnail and
 * smooth-scroll CTA to contact.
 */
import React, { useState, useEffect } from 'react';
import HeroVideoDialog from '@/components/ui/modals/hero-video-dialog.jsx';

export function DemoVideoSection() {
  const [isDarkMode, setIsDarkMode] = useState(false);
  const [isVideoLoading, setIsVideoLoading] = useState(true);

  // Check for dark mode on initial render and when theme changes
  useEffect(() => {
    const checkDarkMode = () => {
      const isDark = document.documentElement.classList.contains('dark');
      setIsDarkMode(isDark);
    };

    // Check on initial render
    checkDarkMode();

    // Set up a MutationObserver to watch for class changes on the html element
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.attributeName === 'class') {
          checkDarkMode();
        }
      });
    });

    observer.observe(document.documentElement, { attributes: true });

    // Clean up the observer on component unmount
    return () => observer.disconnect();
  }, []);

  // Function to handle smooth scrolling to contact section
  const scrollToContact = (e) => {
    e.preventDefault();
    const contactElement = document.getElementById('contact');
    if (contactElement) {
      contactElement.scrollIntoView({ 
        behavior: 'smooth',
        block: 'start'
      });
    }
  };

  // Handle thumbnail loading
  useEffect(() => {
    const img = new Image();
    img.onload = () => setIsVideoLoading(false);
    img.onerror = () => setIsVideoLoading(false);
    img.src = "/images/hero/video.png";
  }, []);

  return (
    <section id="demo-video" className="relative bg-white dark:bg-gray-900 py-24 sm:py-32 px-2 sm:px-6 lg:px-8 scroll-mt-40">
      {/* Background decoration - optimized for Safari scroll performance */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute left-1/2 top-0 -z-10 -translate-x-1/2 blur-sm xl:-top-6" aria-hidden="true">
          <div
            className="aspect-[1155/678] w-[72.1875rem] bg-gradient-to-tr from-blue-200 to-purple-300 opacity-10 dark:from-blue-800 dark:to-purple-900 dark:opacity-15"
            style={{
              // Simplified shape for better performance
              borderRadius: '50%',
              transform: 'translateZ(0)', // Safari optimization
              willChange: 'auto'
            }}
          />
        </div>
      </div>
      
      <div className="mx-auto max-w-7xl">
        {/* Section Header */}
        <div className="mx-auto max-w-3xl sm:max-w-4xl md:mx-auto md:text-center xl:max-w-5xl mb-16">
          <h2 className="text-base font-semibold leading-7 text-purple-600 dark:text-purple-400 text-center">
            View Demo
          </h2>
          <p className="mt-2 text-4xl font-semibold tracking-tight leading-tight text-balance text-gray-900 dark:text-white sm:text-5xl sm:leading-tight mx-auto text-center max-w-[90%]">
            See Advocacy Lab in action
          </p>
          <p className="mt-6 text-lg text-gray-600 dark:text-gray-400 mx-auto text-center max-w-[90%]">
            See firsthand how our platform helps campaigns create professional, high-impact creative in minutes, not hours.
          </p>
        </div>

        {/* Video Player */}
        <div className="mx-auto max-w-5xl">
          <div className="relative">
            {isVideoLoading ? (
              <div className="w-full aspect-video bg-gray-200 dark:bg-gray-700 rounded-lg animate-pulse flex items-center justify-center">
                <div className="w-16 h-16 bg-gray-300 dark:bg-gray-600 rounded-full flex items-center justify-center">
                  <div className="w-0 h-0 border-l-[16px] border-l-gray-400 dark:border-l-gray-500 border-y-[12px] border-y-transparent ml-1"></div>
                </div>
              </div>
            ) : (
              <HeroVideoDialog
                className="w-full"
                animationStyle="from-center"
                videoSrc="https://www.youtube.com/embed/taRKE1O4RDQ?si=4rb-zSdDkVK9qxxb"
                thumbnailSrc="/images/hero/video.png"
                thumbnailAlt="Advocacy Lab Demo Video"
              />
            )}
          </div>
        </div>

        {/* Contact Call to Action */}
        <div className="mx-auto max-w-3xl text-center mt-12">
          <p className="text-gray-600 dark:text-gray-400 mb-6">
            Want a personalized demo or interested in collaborating?
          </p>
          <button
            onClick={scrollToContact}
            className="inline-flex items-center justify-center px-6 py-3 rounded-md bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700 text-white font-semibold text-sm sm:text-base shadow-md hover:shadow-lg transform hover:-translate-y-0.5 transition-all duration-200"
          >
            Contact us
            <svg className="ml-2 w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </button>
        </div>
      </div>
    </section>
  );
} 